/**
 * OwnerRoutes — <Routes> des pages SANS paramètre du shell propriétaire
 * (Sprint routeur, Lot 0+1). Extrait d'App.jsx pour rester sous le cap 500 L.
 *
 * ISO-COMPORTEMENT : chaque <Route> rend exactement le même composant avec les
 * mêmes props que l'ancien switch `currentPage` d'App.jsx. La seule différence :
 * les transitions vers une page routée passent par `goRouted(page)` (setCurrentPage
 * + navigate) au lieu de `setCurrentPage` seul ; les transitions vers une page NON
 * routée (live/health/detail) gardent `setCurrentPage` (état React, Lot 2).
 *
 * Composants enfants AGNOSTIQUES du routeur : ils gardent leurs props onNavigate/
 * onBack ; c'est ici (couche routing) qu'on mappe vers navigate.
 */
import React, { lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { PAGES } from '../constants/pages';
import { PAGE_TO_PATH } from './ownerRouteMap';
import { isGuestSignupRole } from '../utils/trusteePermissions';
import SharedHorses from '../horses/SharedHorses';
import HorseList from '../horses/HorseList';

const SessionListPage      = lazy(() => import('../sessions/SessionListPage'));
const CsvUploadPage        = lazy(() => import('../sessions/CsvUploadPage'));
const HorseDeclarationForm = lazy(() => import('../horses/HorseDeclarationForm'));
const UserProfile          = lazy(() => import('../settings/UserProfile'));
const AdminDashboard       = lazy(() => import('../admin/AdminDashboard'));
const FeedbackPage         = lazy(() => import('../feedback/FeedbackPage'));
const FAQPage              = lazy(() => import('../settings/FAQPage'));
const NoticePage           = lazy(() => import('../settings/NoticePage'));

/**
 * @param s          état applicatif (useAppState)
 * @param onNavigate (page) => handleNavigate + navigate si routée (barre de nav)
 * @param goRouted   (page) => setCurrentPage + navigate-si-besoin (transitions bespoke)
 */
export default function OwnerRoutes({ s, onNavigate, goRouted }) {
  return (
    <Routes>
      <Route path="/import" element={
        <CsvUploadPage horses={[...s.horses, ...(s.sharedHorses || [])]} selectedHorse={s.selectedHorse} sensorType={s.sensorType} setSensorType={s.setSensorType} onSubmit={s.handleFileUpload} darkMode={s.darkMode} uploading={s.uploading} uploadError={s.uploadError} setUploadError={s.setUploadError} currentUser={s.currentUser} getShareRole={s.getShareRole} />
      } />

      <Route path="/sessions" element={
        <SessionListPage sessions={s.sessions} horses={s.horses} selectedHorse={s.selectedHorse} setSelectedHorse={s.setSelectedHorse}
          loadSessions={s.loadSessions} onOpenSession={s.setSelectedSession} onDeleteSession={s.handleDeleteSession} onDeleteSessions={s.handleDeleteSessions} darkMode={s.darkMode} currentUser={s.currentUser} onNavigate={onNavigate}
          getShareRole={s.getShareRole}
          onCompare={(ids) => { s.setCompareSessionIds(ids); s.setCurrentPage(PAGES.COMPARE); }} />
      } />

      <Route path="/horses" element={
        <>
          <SharedHorses darkMode={s.darkMode}
            onViewSharedHorse={(id, role, acceptedAt) => {
              s.setShareRoleForHorse(id, role, acceptedAt);
              s.openHorsePage(id, 'detail', s.setDetailHorse);
            }}
            onViewSessions={(id, role, acceptedAt) => {
              s.setSelectedHorse(id);
              s.loadSessions(id);
              s.setShareRoleForHorse(id, role, acceptedAt);
              goRouted(PAGES.LIST);
            }}
            onViewLive={(horse) => {
              s.setLiveHorse(horse);
              s.setCurrentPage(PAGES.LIVE);
            }}
            onViewHealth={(horse) => {
              s.setHealthHorse(horse);
              s.setCurrentPage(PAGES.HEALTH);
            }}
            onEdit={(id) => s.handleEditHorse(id)}
            onUploadCSV={(id, role, acceptedAt) => {
              s.setSelectedHorse(id);
              s.setShareRoleForHorse(id, role, acceptedAt);
              goRouted(PAGES.UPLOAD);
            }}
          />
          {(!isGuestSignupRole(s.currentUser) || s.horses.length > 0) && (
            <HorseList horses={s.horses} darkMode={s.darkMode}
              onViewData={(id) => { s.setSelectedHorse(id); s.loadSessions(id); goRouted(PAGES.LIST); }}
              onUploadCSV={(id) => { s.setSelectedHorse(id); goRouted(PAGES.UPLOAD); }}
              onEdit={s.handleEditHorse} onDelete={s.handleDeleteHorse}
              onHealth={(id) => s.openHorsePage(id, 'health', s.setHealthHorse)}
              onViewDetail={(id) => s.openHorsePage(id, 'detail', s.setDetailHorse)}
              onLive={(id) => s.openHorsePage(id, 'live', s.setLiveHorse)}
            />
          )}
        </>
      } />

      <Route path="/horses/new" element={
        <HorseDeclarationForm currentUser={s.currentUser} darkMode={s.darkMode}
          onSuccess={() => { s.loadHorses(); goRouted(PAGES.HORSES); }} />
      } />

      <Route path="/account" element={
        <UserProfile currentUser={s.currentUser} onLogout={s.handleLogout} darkMode={s.darkMode} onNavigate={onNavigate} />
      } />

      <Route path="/admin" element={
        <AdminDashboard darkMode={s.darkMode} currentUser={s.currentUser} onViewSession={(id) => { s.setSelectedSession(id); }} />
      } />

      <Route path="/feedback" element={<FeedbackPage darkMode={s.darkMode} />} />

      <Route path="/faq" element={<FAQPage darkMode={s.darkMode} />} />

      <Route path="/notice" element={
        <NoticePage darkMode={s.darkMode} onBack={() => goRouted(PAGES.PROFILE)} />
      } />

      {/* Atterrissage : "/" → page logique courante (préserve LIST vs HORSES de handleLogin) */}
      <Route path="/" element={<Navigate to={PAGE_TO_PATH[s.currentPage] || '/sessions'} replace />} />
      {/* Filet : tout chemin non routé ce lot retombe sur Sessions */}
      <Route path="*" element={<Navigate to="/sessions" replace />} />
    </Routes>
  );
}
